import React from "react";
import { TrendingUp, TrendingDown, DollarSign, Activity, Briefcase, Users, Layers, Percent, UserPlus, Trello } from "lucide-react";

const WorkforceChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    // Helper for icons mapping based on indicator keywords
    const getIcon = (label, color = "currentColor") => {
        const text = label.toLowerCase();
        if (text.includes("employment")) return <Users size={16} className={color} />;
        if (text.includes("unemployment")) return <Activity size={16} className={color} />;
        if (text.includes("wage") || text.includes("payroll")) return <DollarSign size={16} className={color} />;
        if (text.includes("opening")) return <Layers size={16} className={color} />;
        if (text.includes("participation")) return <Percent size={16} className={color} />;
        if (text.includes("hires")) return <UserPlus size={16} className={color} />;
        return <Briefcase size={16} className={color} />;
    };

    // Helper for formatting values based on indicator keywords
    const formatValue = (value, indicator) => {
        if (typeof value === 'string') return value;
        const text = indicator.toLowerCase();
        if (text.includes("rate") || text.includes("participation") || text.includes("%")) {
            return `${(value * 100).toFixed(1)}%`;
        }
        if (text.includes("$") || text.includes("wage") || text.includes("payroll")) {
            if (value > 1000000) return `$${(value / 1000).toFixed(1)}B`; // e.g. Payroll 10,240M
            if (value > 1000) return `$${value.toLocaleString()}`;
            return `$${value}`;
        }
        return value.toLocaleString();
    };

    // Define "Hero" metrics for the top row
    const heroKeys = [
        "Construction Employment - total (jobs)",
        "Average Hourly Wage (Construction)",
        "Construction Unemployment Rate"
    ];

    const heroes = heroKeys.map(key => data.find(item => item.indicator === key)).filter(Boolean);
    const additionalStats = data.filter(item => !heroKeys.includes(item.indicator));

    return (
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-8">
                <div className="d-flex align-items-center gap-2">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                        <Trello size={20} />
                    </div>
                    <h2 className="text-lg font-bold text-gray-800 m-0">Workforce & Labor Market Intelligence</h2>
                </div>
                <div className="d-flex gap-2">
                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full uppercase tracking-tighter shadow-sm border border-blue-100">
                        2025 Data Coverage
                    </span>
                </div>
            </div>

            {/* Hero KPIs Row */}
            <div className="row g-3 mb-8">
                {heroes.map((hero, idx) => (
                    <div key={idx} className="col-4">
                        <div className="p-4 rounded-2xl bg-gray-50 border border-transparent hover:border-blue-100 hover:bg-white transition-all duration-300 h-full group">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="p-1.5 bg-white rounded-lg shadow-sm text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
                                    {getIcon(hero.indicator)}
                                </div>
                                {hero.annual_change && (
                                    <div className={`text-[10px] font-bold d-flex align-items-center gap-0.5 ${hero.annual_change > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {hero.annual_change > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {Math.abs(hero.annual_change * 100).toFixed(1)}%
                                    </div>
                                )}
                            </div>
                            <div className="text-[11px] font-semibold text-gray-500 mb-1 leading-tight">{hero.indicator.replace("Construction ", "").replace(" (Construction)", "")}</div>
                            <div className="text-xl font-black text-gray-900">{formatValue(hero.value, hero.indicator)}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Detailed Stats Grid - SHOWS ADDITIONAL DATA POINTS (NO DUPLICATION) */}
            <div className="flex-grow">
                <div className="p-1 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="p-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center border-b border-gray-200 mb-2">
                        Additional Labor Indicators
                    </div>
                    <div className="max-h-[340px] overflow-y-auto px-2 custom-scrollbar">
                        <div className="row g-2 pb-2">
                            {additionalStats.map((item, idx) => {
                                const isPositive = typeof item.annual_change === 'number' ? item.annual_change > 0 : !item.annual_change.toString().includes("-");
                                const changeVal = typeof item.annual_change === 'number' ? `${(item.annual_change * 100).toFixed(1)}%` : item.annual_change;

                                return (
                                    <div key={idx} className="col-12">
                                        <div className="d-flex align-items-center justify-content-between p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-200 transition-colors shadow-sm">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="p-2 bg-gray-50 rounded-lg text-gray-400">
                                                    {getIcon(item.indicator)}
                                                </div>
                                                <div>
                                                    <div className="text-[12px] font-bold text-gray-800 leading-none mb-1">{item.indicator}</div>
                                                    <div className="text-[10px] text-gray-400 d-flex align-items-center gap-1">
                                                        <Activity size={10} />
                                                        Market Indicator
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[14px] font-black text-gray-900 leading-none mb-1">{formatValue(item.value, item.indicator)}</div>
                                                <div className={`text-[10px] font-bold d-flex align-items-center justify-content-end gap-1 ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                    {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                                                    {changeVal}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-50 d-flex align-items-center justify-content-between text-[11px] font-medium text-gray-400 font-italic">
                <div className="d-flex align-items-center gap-1">
                    <TrendingUp size={12} className="text-emerald-500" />
                    <span>Optimized Data Visibility</span>
                </div>
                <span>Source: BLS / DWS Utah</span>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
            `}</style>
        </div>
    );
};

export default WorkforceChart;
